// Region metadata + filter state.
//
// 8 conventional river-runner regions. Each river is tagged with exactly
// one. The filter state is a Set<Region> stored in localStorage; default
// is "all on" (every region active). Toggling a region narrows the list.

import type { Region } from '../../functions/_lib/types';

export const REGIONS: Array<{ id: Region; label: string; short: string }> = [
  { id: 'pacific-nw',           label: 'Pacific Northwest',         short: 'Pacific NW' },
  { id: 'eastern-cascades',     label: 'Eastern Cascades',          short: 'E. Cascades' },
  { id: 'inland-empire',        label: 'Inland Empire / N. Idaho',  short: 'Inland Empire' },
  { id: 'salmon-river-country', label: 'Salmon River Country',      short: 'Salmon Country' },
  { id: 'greater-yellowstone',  label: 'Greater Yellowstone',       short: 'Greater YNP' },
  { id: 'colorado-plateau',     label: 'Colorado Plateau',          short: 'Colo. Plateau' },
  { id: 'southern-rockies',     label: 'Southern Rockies',          short: 'S. Rockies' },
  { id: 'sierra-nevada',        label: 'Sierra Nevada',             short: 'Sierra' },
];

const KEY = 'frozen-fly:regions-v1';

type RegionListener = (regions: Set<Region>) => void;
let activeRegions: Set<Region> = loadInitial();
const listeners = new Set<RegionListener>();

function loadInitial(): Set<Region> {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const arr = JSON.parse(raw) as string[];
      if (Array.isArray(arr) && arr.length > 0) {
        return new Set(arr.filter((r) => REGIONS.some((R) => R.id === r)) as Region[]);
      }
    }
  } catch { /* ignore */ }
  // Default: all regions on
  return new Set(REGIONS.map((r) => r.id));
}

export function getActiveRegions(): Set<Region> {
  return new Set(activeRegions);
}

export function setActiveRegions(next: Set<Region>): void {
  // Refuse empty set — bounce to all on.
  const safe = next.size === 0 ? new Set(REGIONS.map((r) => r.id)) : next;
  if (sameSet(safe, activeRegions)) return;
  activeRegions = safe;
  try { localStorage.setItem(KEY, JSON.stringify([...activeRegions])); } catch { /* ignore */ }
  for (const l of listeners) l(getActiveRegions());
}

export function toggleRegion(r: Region): void {
  const next = new Set(activeRegions);
  if (next.has(r)) next.delete(r); else next.add(r);
  setActiveRegions(next);
}

export function onRegionChange(fn: RegionListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function sameSet(a: Set<Region>, b: Set<Region>): boolean {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}
